import {
	DatePicker,
	Field,
	Input,
	InputNationalIdentity,
	InputSurname,
	NO_SURNAME_VALUE,
	toaster,
} from '@welbe/design-system';
import { CustomSelectField } from 'App/Components/CustomSelect/CustomSelectField';
import { GENERIC_CURP } from 'constants/genericNationalIdentity';
import { useEffect, useMemo, useState } from 'react';
import { EmployeeSection } from '../../components/EmployeeSection';
import {
	BIOLOGICAL_SEX_OPTIONS,
	CURP_SEX_OPTIONS,
	GENDER_OPTIONS,
	MEXICO_CODE,
} from '../../Employee.constants';
import { useEmployee } from '../../Employee.hooks';
import { Controller } from 'react-hook-form';
import { getNationalitiesCatalog } from 'services/services';

const CURP_SEX_MAP: Record<string, string> = {
	H: 'HOMBRE',
	M: 'MUJER',
	X: 'NO_BINARIO',
};

export const PersonalData = ({
	errors,
	fieldErrors,
	disabled,
}: {
	errors: string[];
	fieldErrors: Record<string, any>;
	disabled?: boolean;
}) => {
	const [isOlderThan120, setOlderThan120] = useState(false);
	const [loadingNationalities, setLoadingNationalities] = useState(false);
	const [nationalities, setNationalities] = useState([]);

	const checkAge = (birthDate?: string) => {
		setOlderThan120(false);
		if (!birthDate) return;

		const [year, month, day] = birthDate.split('-').map(Number);
		const birthDateObj = new Date(year, month - 1, day);

		if (isNaN(birthDateObj.getTime())) return;

		const today = new Date();

		const cutoffDate = new Date(
			today.getFullYear() - 120,
			today.getMonth(),
			today.getDate(),
		);

		if (birthDateObj <= cutoffDate) {
			setOlderThan120(true);
			toaster.create({
				title: 'Edad inválida',
				description: 'La edad debe ser menor a 120 años',
				type: 'error',
			});
		}
	};

	const {
		methods,
		countries,
		federalEntities,
		loadingCountries,
		loadingFederalEntities,
	} = useEmployee();

	const { control, register, setValue, watch } = methods;

	const watchedCountryCode = watch('siresProfile.countryCode');
	const watchedFederalEntityCode = watch('siresProfile.federalEntityCode');
	const watchedNationalIdentity = watch('nationalIdentity');

	const selectedFederalEntity = useMemo(() => {
		return federalEntities.find(
			(e: any) => e.id === watchedFederalEntityCode?.toString(),
		);
	}, [federalEntities, watchedFederalEntityCode]);

	const isCurpIgnored =
		watchedCountryCode?.toString() === MEXICO_CODE &&
		selectedFederalEntity?.name === 'SE IGNORA';

	const isFederalEntityUnspecified =
		selectedFederalEntity?.name === 'NO ESPECIFICADO';

	const isGenericCurp =
		isCurpIgnored || Boolean(watch('hasGenericNationalIdentity'));

	useEffect(() => {
		if (isCurpIgnored && watchedNationalIdentity !== 'XXXX999999XXXXXX99') {
			setValue('nationalIdentity', 'XXXX999999XXXXXX99');
		}
	}, [isCurpIgnored, watchedNationalIdentity, setValue]);

	useEffect(() => {
		if (isGenericCurp || !watchedNationalIdentity) return;

		const sexChar = watchedNationalIdentity[10]?.toUpperCase();
		const sexValue = CURP_SEX_MAP[sexChar];

		if (sexValue) {
			setValue('siresProfile.nationalIdentityGender', sexValue);
		}
	}, [watchedNationalIdentity, isGenericCurp, setValue]);

	const fetchNationalities = async () => {
		try {
			setLoadingNationalities(true);
			const { data } = await getNationalitiesCatalog();
			setNationalities(
				data.content.nationalities.map(
					(nationality: { code: number; nationality: string }) => ({
						id: nationality.code.toString(),
						name: nationality.nationality,
					}),
				),
			);
		} catch (error) {
			console.error(error);
		} finally {
			setLoadingNationalities(false);
		}
	};

	useEffect(() => {
		fetchNationalities();
	}, []);

	return (
		<EmployeeSection
			id="personal-data"
			title="Datos personales"
			description="Llena los campos con la información del colaborador"
		>
			<Field.Root
				required
				invalid={errors.includes('firstName')}
				readOnly={disabled}
			>
				<Field.Label>
					Nombre <Field.RequiredIndicator />
				</Field.Label>
				<Input
					placeholder="Escribe el nombre"
					{...register('firstName', {
						required: 'Nombre es requerido',
					})}
				/>
				<Field.ErrorText>{fieldErrors?.firstName}</Field.ErrorText>
			</Field.Root>
			<Controller
				name="middleName"
				control={control}
				render={({ field }) => (
					<InputSurname.Snippet
						required
						label="Primer apellido"
						placeholder="Escribe el primer apellido"
						checkboxText="No cuenta con primer apellido"
						invalid={errors.includes('middleName')}
						errorText={fieldErrors?.middleName}
						disabled={disabled || Boolean(watch('hasNoMiddleName'))}
						value={field.value || ''}
						onChange={(value) =>
							field.onChange(value === NO_SURNAME_VALUE ? undefined : value)
						}
						checked={Boolean(watch('hasNoMiddleName'))}
						onCheckedChange={(checked) => {
							setValue('hasNoMiddleName', checked);
							if (checked) field.onChange('');
						}}
					/>
				)}
			/>
			<Controller
				name="lastName"
				control={control}
				render={({ field }) => (
					<InputSurname.Snippet
						required
						label="Segundo apellido"
						placeholder="Escribe el segundo apellido"
						checkboxText="No cuenta con segundo apellido"
						invalid={errors.includes('lastName')}
						errorText={fieldErrors?.lastName}
						disabled={disabled || Boolean(watch('hasNoLastName'))}
						value={field.value || ''}
						onChange={(value) =>
							field.onChange(value === NO_SURNAME_VALUE ? undefined : value)
						}
						checked={Boolean(watch('hasNoLastName'))}
						onCheckedChange={(checked) => {
							setValue('hasNoLastName', checked);
							if (checked) field.onChange('');
						}}
					/>
				)}
			/>
			<Controller
				name="birthDate"
				control={control}
				render={({ field }) => {
					let errorText = undefined;

					if (errors.includes('birthDate') && isOlderThan120 && field.value) {
						errorText = 'La edad debe ser menor a 120 años';
					}

					return (
						<DatePicker.Snippet
							required
							max={new Date().toISOString().split('T')[0]}
							readOnly={disabled}
							invalid={errors.includes('birthDate') || isOlderThan120}
							label="Fecha de nacimiento"
							errorText={errorText}
							placeholder="Selecciona una fecha"
							value={field.value ? field.value : undefined}
							onChange={(date) => {
								field.onChange(date?.split('/').reverse().join('-'));
								checkAge(date?.split('/').reverse().join('-'));
							}}
						/>
					);
				}}
			/>
			<Controller
				name="siresProfile.countryCode"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Pais de nacimiento"
						required
						readOnly={disabled}
						invalid={errors.includes('siresProfile') && !field.value}
						options={countries}
						value={field.value != null ? String(field.value) : ''}
						onChange={(id) => {
							setValue(
								'siresProfile.federalEntityCode',
								id !== MEXICO_CODE ? '0' : undefined,
							);
							field.onChange(Number(id));
						}}
						placeholder="Seleccione una opción"
						loading={loadingCountries}
					/>
				)}
			/>

			<Controller
				name="siresProfile.federalEntityCode"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Entidad de nacimiento"
						required
						readOnly={disabled}
						disabled={String(field.value) === '0'}
						invalid={errors.includes('siresProfile') && !field.value}
						options={federalEntities}
						value={field.value != null ? String(field.value) : ''}
						onChange={(id) => field.onChange(Number(id))}
						placeholder="Seleccione una opción"
						loading={loadingFederalEntities}
					/>
				)}
			/>

			<Controller
				name="nationalIdentity"
				control={control}
				render={({ field }) => (
					<InputNationalIdentity
						readOnly={disabled || isCurpIgnored}
						checked={Boolean(watch('hasGenericNationalIdentity'))}
						invalid={
							errors.includes('nationalIdentity') && Boolean(!field.value)
						}
						checkboxDisabled={!isFederalEntityUnspecified}
						value={field.value}
						onChange={(value: any) =>
							field.onChange(value === GENERIC_CURP ? undefined : value)
						}
						onCheckedChange={(checked) => {
							setValue('hasGenericNationalIdentity', checked);
							if (checked) field.onChange(undefined);
						}}
					/>
				)}
			/>

			<Controller
				name="siresProfile.nationalityCode"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Nacionalidad"
						required
						readOnly={disabled}
						invalid={errors.includes('siresProfile') && !field.value}
						options={nationalities}
						value={field.value != null ? String(field.value) : ''}
						onChange={(id) => field.onChange(Number(id))}
						placeholder="Seleccione una opción"
						loading={loadingNationalities}
					/>
				)}
			/>

			<Controller
				name="siresProfile.nationalIdentityGender"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Sexo de acuerdo con el CURP"
						required
						readOnly={disabled}
						disabled={!isGenericCurp}
						invalid={errors.includes('siresProfile') && !field.value}
						options={CURP_SEX_OPTIONS}
						value={field.value || ''}
						onChange={field.onChange}
						placeholder="Selecciona el sexo"
					/>
				)}
			/>

			<Controller
				name="siresProfile.biologicalSex"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Sexo biológico"
						required
						readOnly={disabled}
						invalid={errors.includes('siresProfile') && !field.value}
						options={BIOLOGICAL_SEX_OPTIONS}
						value={field.value || ''}
						onChange={field.onChange}
						placeholder="Selecciona el sexo"
					/>
				)}
			/>

			<Controller
				name="siresProfile.gender"
				control={control}
				render={({ field }) => (
					<CustomSelectField
						label="Género"
						required
						readOnly={disabled}
						invalid={errors.includes('siresProfile') && !field.value}
						options={GENDER_OPTIONS}
						value={field.value || ''}
						onChange={field.onChange}
						placeholder="Selecciona el género"
					/>
				)}
			/>
		</EmployeeSection>
	);
};
